import type { MonthBucket, OrderRow, RawRow, StateBucket } from "./types.js";

const GROUP_KEY = "Order name";
const PRODUCT_COL = "Product title at time of sale";
const QTY_COL = "Orders";
const AMOUNT_COL = "Total sales";

const SUM_COLS = new Set([
  "Orders",
  "Gross sales",
  "Discounts",
  "Returns",
  "Net sales",
  "Shipping charges",
  "Duties",
  "Additional fees",
  "Taxes",
  "Total sales",
]);

const JOIN_COLS = new Set(["Sale ID", PRODUCT_COL]);

export const US_STATES: Record<string, string> = {
  Alabama: "AL",
  Alaska: "AK",
  Arizona: "AZ",
  Arkansas: "AR",
  California: "CA",
  Colorado: "CO",
  Connecticut: "CT",
  Delaware: "DE",
  "District of Columbia": "DC",
  Florida: "FL",
  Georgia: "GA",
  Hawaii: "HI",
  Idaho: "ID",
  Illinois: "IL",
  Indiana: "IN",
  Iowa: "IA",
  Kansas: "KS",
  Kentucky: "KY",
  Louisiana: "LA",
  Maine: "ME",
  Maryland: "MD",
  Massachusetts: "MA",
  Michigan: "MI",
  Minnesota: "MN",
  Mississippi: "MS",
  Missouri: "MO",
  Montana: "MT",
  Nebraska: "NE",
  Nevada: "NV",
  "New Hampshire": "NH",
  "New Jersey": "NJ",
  "New Mexico": "NM",
  "New York": "NY",
  "North Carolina": "NC",
  "North Dakota": "ND",
  Ohio: "OH",
  Oklahoma: "OK",
  Oregon: "OR",
  Pennsylvania: "PA",
  "Rhode Island": "RI",
  "South Carolina": "SC",
  "South Dakota": "SD",
  Tennessee: "TN",
  Texas: "TX",
  Utah: "UT",
  Vermont: "VT",
  Virginia: "VA",
  Washington: "WA",
  "West Virginia": "WV",
  Wisconsin: "WI",
  Wyoming: "WY",
};

function toNum(v: unknown): number {
  if (v == null || v === "") return 0;
  if (typeof v === "number") return v;
  const n = Number(v);
  return isNaN(n) ? 0 : n;
}

function toStr(v: unknown): string {
  if (v == null) return "";
  return String(v);
}

/**
 * Build "(qty) Product\n(qty) Product" map keyed by order name from the raw
 * rows. Matches the legacy Python script's `build_summary_dict`.
 */
function buildSummaryMap(rows: RawRow[]): Map<string, string> {
  const counts = new Map<string, Map<string, number>>();
  for (const row of rows) {
    const product = toStr(row[PRODUCT_COL]).trim();
    if (!product) continue;
    const qty = toNum(row[QTY_COL]);
    if (qty <= 0) continue;
    const order = toStr(row[GROUP_KEY]);
    if (!order) continue;
    let inner = counts.get(order);
    if (!inner) {
      inner = new Map();
      counts.set(order, inner);
    }
    inner.set(product, (inner.get(product) ?? 0) + qty);
  }
  const out = new Map<string, string>();
  for (const [order, inner] of counts) {
    const lines: string[] = [];
    for (const [product, qty] of inner) {
      lines.push(`(${Math.trunc(qty)}) ${product}`);
    }
    out.set(order, lines.join("\n"));
  }
  return out;
}

/**
 * Group raw rows by `Order name`. Sum money columns, newline-join product
 * titles and sale IDs, take `first` for everything else. Preserves
 * first-seen ordering of orders.
 */
export function groupOrders(rows: RawRow[]): OrderRow[] {
  const summaryMap = buildSummaryMap(rows);
  const groups = new Map<string, OrderRow>();

  for (const row of rows) {
    const orderName = toStr(row[GROUP_KEY]);
    if (!orderName) continue;
    let g = groups.get(orderName);
    if (!g) {
      g = { [GROUP_KEY]: orderName } as OrderRow;
      // Initialize all columns from this row first
      for (const [col, val] of Object.entries(row)) {
        if (col === GROUP_KEY) continue;
        if (SUM_COLS.has(col)) {
          (g as any)[col] = toNum(val);
        } else if (JOIN_COLS.has(col)) {
          (g as any)[col] = val == null || val === "" ? "" : String(val);
        } else {
          (g as any)[col] = val;
        }
      }
      groups.set(orderName, g);
    } else {
      for (const [col, val] of Object.entries(row)) {
        if (col === GROUP_KEY) continue;
        if (SUM_COLS.has(col)) {
          (g as any)[col] = ((g as any)[col] ?? 0) + toNum(val);
        } else if (JOIN_COLS.has(col)) {
          if (val == null || val === "") continue;
          const existing = (g as any)[col] as string | undefined;
          (g as any)[col] = existing ? `${existing}\n${String(val)}` : String(val);
        }
        // 'first' columns: leave alone
      }
    }
  }

  // Round money columns to 2 decimals; attach summary; detect replacement orders.
  const moneyCols = Array.from(SUM_COLS).filter((c) => c !== "Orders");
  const result: OrderRow[] = [];
  for (const [orderName, g] of groups) {
    for (const c of moneyCols) {
      const v = (g as any)[c];
      if (typeof v === "number") {
        (g as any)[c] = Math.round(v * 100) / 100;
      }
    }
    const summary = summaryMap.get(orderName) ?? "";
    // Flag replacement / refund-only orders by Gross sales == 0 (matches
    // Shopify Tax Report rows that have only shipping + tax). Fall back to
    // Total sales == 0 for legacy "cleaned" inputs.
    const gross = toNum((g as any)["Gross sales"]);
    const totalSales = toNum((g as any)[AMOUNT_COL]);
    g.isReplacement = Math.abs(gross) < 0.005 || Math.abs(totalSales) < 0.005;
    g["Order Summary"] = summary || (g.isReplacement ? "Replacement order" : "");
    result.push(g);
  }
  return result;
}

function monthLabel(year: number, month: number): string {
  const names = [
    "Jan", "Feb", "Mar", "Apr", "May", "Jun",
    "Jul", "Aug", "Sep", "Oct", "Nov", "Dec",
  ];
  return `${names[month]} ${year}`;
}

/**
 * Filter to US-state orders and bucket by `Shipping region` → `(year, month)`.
 * Returns one StateBucket per US state present in the data, sorted alphabetically.
 */
export function bucketByStateAndMonth(orders: OrderRow[]): StateBucket[] {
  const byState = new Map<string, Map<string, MonthBucket>>();

  for (const o of orders) {
    const state = toStr((o as any)["Shipping region"]).trim();
    if (!state || !(state in US_STATES)) continue;
    const day = (o as any)["Day"] as { y: number; m: number; d: number } | null;
    if (!day) continue;
    const year = day.y;
    const month = day.m - 1; // CalendarDate stores 1-based months
    const key = `${year}-${month}`;

    let monthMap = byState.get(state);
    if (!monthMap) {
      monthMap = new Map();
      byState.set(state, monthMap);
    }
    let bucket = monthMap.get(key);
    if (!bucket) {
      bucket = {
        year,
        month,
        monthLabel: monthLabel(year, month),
        orders: [],
      };
      monthMap.set(key, bucket);
    }
    bucket.orders.push(o);
  }

  const result: StateBucket[] = [];
  for (const [state, monthMap] of byState) {
    const months = Array.from(monthMap.values()).sort(
      (a, b) => a.year - b.year || a.month - b.month,
    );
    // Sort orders within each month: replacements first, then by day, then by order name.
    for (const m of months) {
      m.orders.sort((a, b) => {
        if (a.isReplacement !== b.isReplacement) return a.isReplacement ? -1 : 1;
        const ad = a["Day"];
        const bd = b["Day"];
        const ak = ad ? ad.y * 10000 + ad.m * 100 + ad.d : 0;
        const bk = bd ? bd.y * 10000 + bd.m * 100 + bd.d : 0;
        if (ak !== bk) return ak - bk;
        return String(a["Order name"]).localeCompare(String(b["Order name"]));
      });
    }
    const totalOrders = months.reduce((s, m) => s + m.orders.length, 0);
    result.push({
      state,
      stateAbbrev: US_STATES[state],
      months,
      totalOrders,
    });
  }
  result.sort((a, b) => a.state.localeCompare(b.state));
  return result;
}
